import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Upload, Trash2, Trash, Download, ChevronDown, Plus } from 'lucide-react';
import { clientAllowlistApi, eventsApi, adminApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { AppUser, Event } from '../../types';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, assemblePhone } from '../../config/countryCodes';

type Tab = 'email' | 'phone';

interface UploadConflictModalProps {
  onReplace: () => void;
  onAppend: () => void;
  onCancel: () => void;
  type: Tab;
}

const UploadConflictModal: React.FC<UploadConflictModalProps> = ({ onReplace, onAppend, onCancel, type }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
      <h3 className="text-base font-semibold text-gray-900 mb-2">Existing {type} list found</h3>
      <p className="text-sm text-gray-600 mb-5">
        There are already entries in the {type} allowlist. What would you like to do with the newly uploaded CSV?
      </p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={onAppend} className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50">
          Append to existing
        </button>
        <button onClick={onReplace} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
          Replace entire list
        </button>
      </div>
    </div>
  </div>
);

interface ConfirmModalProps {
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ message, confirmLabel, onConfirm, onCancel, danger }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
      <p className="text-sm text-gray-700 mb-5">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-3 py-1.5 text-sm text-white rounded-md ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const Allowlist: React.FC = () => {
  const { isAdmin } = useAuth();

  // Admin-only: client list and selected client
  const [clients, setClients] = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientsLoading, setClientsLoading] = useState(false);

  // Events
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [eventsLoading, setEventsLoading] = useState(true);

  // Tab
  const [activeTab, setActiveTab] = useState<Tab>('email');

  // Settings
  const [useEmailAllowlist, setUseEmailAllowlist] = useState(false);
  const [usePhoneAllowlist, setUsePhoneAllowlist] = useState(false);
  const [emailCount, setEmailCount] = useState(0);
  const [phoneCount, setPhoneCount] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // List
  const [values, setValues] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [listLoading, setListLoading] = useState(false);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Upload
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [invalidEntries, setInvalidEntries] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Confirm modals
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Manual add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEmailValue, setAddEmailValue] = useState('');
  const [addCountryCode, setAddCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [addLocalNumber, setAddLocalNumber] = useState('');
  const [adding, setAdding] = useState(false);

  // ─── Admin: fetch all clients on mount ──────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    setClientsLoading(true);
    adminApi.getAllUsers({ role: 'client', limit: 100 }).then((res: any) => {
      if (res.data.success && res.data.data) {
        const list: AppUser[] = res.data.data.users ?? [];
        setClients(list);
        if (list.length > 0) setSelectedClientId(list[0]._id);
      }
      setClientsLoading(false);
    }).catch(() => {
      toast.error('Failed to load clients');
      setClientsLoading(false);
    });
  }, [isAdmin]);

  // ─── Fetch events when client selection changes (admin) or on mount (client) ─
  useEffect(() => {
    if (isAdmin && !selectedClientId) return;
    setEventsLoading(true);
    setSelectedEventId('');
    setEvents([]);

    const fetchAllPages = async () => {
      const PAGE_LIMIT = 100;
      let page = 1;
      let all: Event[] = [];
      while (true) {
        const baseParams = isAdmin ? { clientId: selectedClientId } : {};
        const res = await eventsApi.getAll({ ...baseParams, page, limit: PAGE_LIMIT });
        if (!res.data.success || !res.data.data) break;
        const batch = res.data.data as Event[];
        all = [...all, ...batch];
        if (batch.length < PAGE_LIMIT) break;
        page++;
      }
      return all;
    };

    fetchAllPages().then((list) => {
      setEvents(list);
      if (list.length > 0) setSelectedEventId(list[0]._id);
      setEventsLoading(false);
    }).catch(() => {
      toast.error('Failed to load events');
      setEventsLoading(false);
    });
  }, [isAdmin, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch settings when event or tab changes ────────────────────────────────
  const fetchSettings = useCallback(async () => {
    if (!selectedEventId) return;
    setSettingsLoading(true);
    try {
      const res = await clientAllowlistApi.getSettings(selectedEventId);
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setUseEmailAllowlist(d.useEmailAllowlist);
        setUsePhoneAllowlist(d.usePhoneAllowlist);
        setEmailCount(d.emailCount);
        setPhoneCount(d.phoneCount);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to load allowlist settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [selectedEventId]);

  const fetchList = useCallback(async (page = pagination.page) => {
    if (!selectedEventId) return;
    setListLoading(true);
    try {
      const res = await clientAllowlistApi.getList(selectedEventId, activeTab, { page, limit: pagination.limit });
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setValues(d.values || []);
        setPagination((prev) => ({ ...prev, page, total: d.pagination?.total ?? 0 }));
      }
    } catch {
      setValues([]);
    } finally {
      setListLoading(false);
    }
  }, [selectedEventId, activeTab, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEventId) return;
    setSelected(new Set());
    setInvalidEntries([]);
    setShowAddForm(false);
    setAddEmailValue('');
    setAddLocalNumber('');
    setAddCountryCode(DEFAULT_COUNTRY_CODE);
    setPagination((p) => ({ ...p, page: 1 }));
    fetchSettings();
    fetchList(1);
  }, [selectedEventId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCount = activeTab === 'email' ? emailCount : phoneCount;
  const currentToggle = activeTab === 'email' ? useEmailAllowlist : usePhoneAllowlist;

  // ─── Manual add entry ────────────────────────────────────────────────────────
  const handleAddEntry = async () => {
    if (!selectedEventId) return;
    const value = activeTab === 'email'
      ? addEmailValue.trim()
      : assemblePhone(addCountryCode, addLocalNumber.trim());
    if (!value) return;
    setAdding(true);
    try {
      const res = await clientAllowlistApi.addEntry(selectedEventId, activeTab, value);
      if (res.data.success && res.data.data) {
        if (activeTab === 'email') setEmailCount(res.data.data.totalCount);
        else setPhoneCount(res.data.data.totalCount);
        setAddEmailValue('');
        setAddLocalNumber('');
        setAddCountryCode(DEFAULT_COUNTRY_CODE);
        setShowAddForm(false);
        fetchList(1);
        toast.success(`${activeTab === 'email' ? 'Email' : 'Phone number'} added to allowlist.`);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  // ─── Toggle ──────────────────────────────────────────────────────────────────
  const handleToggle = async (checked: boolean) => {
    if (!selectedEventId) return;
    setSaving(true);
    try {
      const payload = activeTab === 'email'
        ? { useEmailAllowlist: checked }
        : { usePhoneAllowlist: checked };
      const res = await clientAllowlistApi.updateSettings(selectedEventId, payload);
      if (res.data.success) {
        if (activeTab === 'email') setUseEmailAllowlist(checked);
        else setUsePhoneAllowlist(checked);
        toast.success(checked
          ? `${activeTab === 'email' ? 'Email' : 'Phone'} allowlist enabled.`
          : `${activeTab === 'email' ? 'Email' : 'Phone'} allowlist disabled.`);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // ─── CSV upload flow ─────────────────────────────────────────────────────────
  const doUpload = async (file: File, action: 'replace' | 'append') => {
    setUploading(true);
    try {
      const res = await clientAllowlistApi.uploadCsv(selectedEventId, activeTab, file, action);
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        if (activeTab === 'email') setEmailCount(d.totalCount);
        else setPhoneCount(d.totalCount);
        setInvalidEntries(d.invalid || []);
        setSelected(new Set());
        fetchList(1);
        const msg = `${d.uploaded} added, ${d.skippedDuplicates} duplicate(s) skipped${d.invalid.length ? `, ${d.invalid.length} invalid` : ''}.`;
        toast.success(msg);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setPendingFile(null);
    }
  };

  const handleFileChange = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file.');
      return;
    }
    if (currentCount > 0) {
      setPendingFile(file);
      setShowConflictModal(true);
    } else {
      doUpload(file, 'replace');
    }
  };

  const handleConflictReplace = () => {
    setShowConflictModal(false);
    if (pendingFile) doUpload(pendingFile, 'replace');
  };

  const handleConflictAppend = () => {
    setShowConflictModal(false);
    if (pendingFile) doUpload(pendingFile, 'append');
  };

  const handleConflictCancel = () => {
    setShowConflictModal(false);
    setPendingFile(null);
  };

  // ─── Download sample CSV ─────────────────────────────────────────────────────
  const downloadSample = () => {
    const content = activeTab === 'email'
      ? 'email\nexample@domain.com\nuser@company.org\n'
      : 'phone\n+1234567890\n+919876543210\n';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample-${activeTab}-allowlist.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Selection ───────────────────────────────────────────────────────────────
  const allPageSelected = values.length > 0 && values.every((v) => selected.has(v));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      const next = new Set(selected);
      values.forEach((v) => next.delete(v));
      setSelected(next);
    } else {
      const next = new Set(selected);
      values.forEach((v) => next.add(v));
      setSelected(next);
    }
  };

  const toggleSelectOne = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSelected(next);
  };

  // ─── Delete selected ─────────────────────────────────────────────────────────
  const handleDeleteSelected = async () => {
    setConfirmDeleteSelected(false);
    setDeleting(true);
    try {
      const valuesToDelete = [...selected];
      const res = await clientAllowlistApi.deleteEntries(selectedEventId, activeTab, valuesToDelete);
      if (res.data.success && res.data.data) {
        if (activeTab === 'email') setEmailCount(res.data.data.totalCount);
        else setPhoneCount(res.data.data.totalCount);
        setSelected(new Set());
        fetchList(1);
        toast.success(`${res.data.data.deleted} entry(s) deleted and user(s) deactivated.`);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Clear all ───────────────────────────────────────────────────────────────
  const handleClearAll = async () => {
    setConfirmClearAll(false);
    setClearing(true);
    try {
      const res = await clientAllowlistApi.clearList(selectedEventId, activeTab);
      if (res.data.success && res.data.data) {
        if (activeTab === 'email') setEmailCount(0);
        else setPhoneCount(0);
        setValues([]);
        setSelected(new Set());
        setPagination((p) => ({ ...p, page: 1, total: 0 }));
        toast.success(`${res.data.data.deleted} entry(s) cleared and user(s) deactivated.`);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Clear failed');
    } finally {
      setClearing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  if (clientsLoading || eventsLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Modals */}
      {showConflictModal && (
        <UploadConflictModal
          type={activeTab}
          onReplace={handleConflictReplace}
          onAppend={handleConflictAppend}
          onCancel={handleConflictCancel}
        />
      )}
      {confirmDeleteSelected && (
        <ConfirmModal
          message={`Delete ${selected.size} selected entry(s)? The corresponding user account(s) for this event will be deactivated.`}
          confirmLabel="Delete & deactivate"
          onConfirm={handleDeleteSelected}
          onCancel={() => setConfirmDeleteSelected(false)}
          danger
        />
      )}
      {confirmClearAll && (
        <ConfirmModal
          message={`Clear the entire ${activeTab} allowlist for this event? All ${currentCount} entry(s) will be removed and matching user accounts will be deactivated.`}
          confirmLabel="Clear all"
          onConfirm={handleClearAll}
          onCancel={() => setConfirmClearAll(false)}
          danger
        />
      )}

      {/* Title */}
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">Signup / Sign-in Allowlist</h1>
      </div>
      <p className="text-sm text-gray-500 -mt-3">
        Restrict who can sign up or sign in. Upload a CSV of allowed entries and turn on the switch to enforce the list.
      </p>

      {/* Filters: client (admin only) + event */}
      <div className="flex flex-wrap items-center gap-4">
        {isAdmin && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Client</label>
            <div className="relative">
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-md bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              >
                {clients.length === 0 && <option value="">No clients found</option>}
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>{c.name} ({c.email})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Event</label>
          <div className="relative">
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              disabled={events.length === 0}
              className="pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-md bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[220px] disabled:opacity-50"
            >
              {events.length === 0 && <option value="">No events found</option>}
              {events.map((ev) => (
                <option key={ev._id} value={ev._id}>{ev.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {(['email', 'phone'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'email' ? 'Email Allowlist' : 'Phone Allowlist'}
            <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === tab ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {tab === 'email' ? emailCount : phoneCount}
            </span>
          </button>
        ))}
      </div>

      {settingsLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Settings card */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Settings</h2>
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleToggle(!currentToggle)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${currentToggle ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentToggle ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-gray-700">
                {currentToggle
                  ? `Only ${activeTab === 'email' ? 'emails' : 'phone numbers'} in the allowlist can sign up or sign in.`
                  : `${activeTab === 'email' ? 'Email' : 'Phone'} allowlist is not enforced.`}
              </span>
            </div>
            <p className="text-xs text-gray-400">{currentCount} {activeTab === 'email' ? 'email(s)' : 'phone number(s)'} in this list.</p>
          </div>

          {/* Upload card */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Upload CSV</h2>
            <p className="text-sm text-gray-500">
              {activeTab === 'email'
                ? 'CSV with one email per line, or a column named "email". Invalid emails are reported.'
                : 'CSV with one phone number per line (e.g. +1234567890), or a column named "phone". Invalid entries are reported.'}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <label className={`inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload CSV'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileChange(file);
                    e.target.value = '';
                  }}
                />
              </label>
              <button
                onClick={downloadSample}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Download sample
              </button>
            </div>

            {/* Invalid entries from last upload */}
            {invalidEntries.length > 0 && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-xs font-medium text-amber-700 mb-1">{invalidEntries.length} invalid {activeTab === 'email' ? 'email(s)' : 'phone number(s)'} were skipped:</p>
                <ul className="text-xs text-amber-600 space-y-0.5 max-h-24 overflow-y-auto">
                  {invalidEntries.map((e, i) => <li key={i} className="font-mono">{e}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-700">
                {activeTab === 'email' ? 'Allowed emails' : 'Allowed phone numbers'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddForm((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                >
                  <Plus className="w-3 h-3" />
                  Add {activeTab === 'email' ? 'email' : 'phone'}
                </button>
                {selected.size > 0 && (
                  <button
                    onClick={() => setConfirmDeleteSelected(true)}
                    disabled={deleting}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete selected ({selected.size})
                  </button>
                )}
                {currentCount > 0 && (
                  <button
                    onClick={() => setConfirmClearAll(true)}
                    disabled={clearing}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash className="w-3 h-3" />
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => { fetchSettings(); fetchList(1); }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
            </div>

            {/* Inline add form */}
            {showAddForm && (
              <div className="px-4 py-3 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center gap-2 flex-wrap">
                  {activeTab === 'email' ? (
                    <input
                      type="email"
                      value={addEmailValue}
                      onChange={(e) => setAddEmailValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddEntry()}
                      placeholder="name@example.com"
                      className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      autoFocus
                    />
                  ) : (
                    <>
                      <select
                        value={addCountryCode}
                        onChange={(e) => setAddCountryCode(e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={addLocalNumber}
                        onChange={(e) => setAddLocalNumber(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddEntry()}
                        placeholder="10-digit number"
                        className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        autoFocus
                      />
                    </>
                  )}
                  <button
                    onClick={handleAddEntry}
                    disabled={adding || (activeTab === 'email' ? !addEmailValue.trim() : !addLocalNumber.trim())}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {adding ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    onClick={() => { setShowAddForm(false); setAddEmailValue(''); setAddLocalNumber(''); }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 bg-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {listLoading ? (
              <div className="py-8 flex items-center justify-center">
                <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : values.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">No entries yet. Upload a CSV to get started.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={toggleSelectAll}
                            className="rounded border-gray-300 text-blue-600"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {activeTab === 'email' ? 'Email' : 'Phone'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {values.map((v, i) => (
                        <tr key={i} className={`hover:bg-gray-50 ${selected.has(v) ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selected.has(v)}
                              onChange={() => toggleSelectOne(v)}
                              className="rounded border-gray-300 text-blue-600"
                            />
                          </td>
                          <td className="px-4 py-2 text-gray-700 font-mono text-xs">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">Total: {pagination.total} entries</p>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => fetchList(pagination.page - 1)}
                      className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-white"
                    >
                      Prev
                    </button>
                    <span className="text-xs text-gray-600">Page {pagination.page} of {totalPages}</span>
                    <button
                      disabled={pagination.page >= totalPages}
                      onClick={() => fetchList(pagination.page + 1)}
                      className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-white"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Allowlist;
